/**
 * Navigation-level persona gate.
 *
 * This is NOT a security boundary. It decides what a user is shown; the BFF
 * re-authorises every request against Open edX regardless of what the client
 * believes. Treating this as enforcement is the classic way to ship an admin
 * panel that anyone can reach by typing the URL.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Persona } from '../lib/session';

const RANK: Record<Persona, number> = {
  anonymous: 0,
  learner: 1,
  // Staff sit between learner and admin: they author and grade, but cannot grant
  // another person access. That is the OrgStaffRole / OrgInstructorRole split.
  tenant_staff: 2,
  tenant_admin: 3,
  superadmin: 4,
};

interface Props {
  min: Persona;
  persona: Persona;
  children?: React.ReactNode;
}

export function RequirePersona({ min, persona, children }: Props) {
  const location = useLocation();

  if (RANK[persona] < RANK[min]) {
    if (persona === 'anonymous') {
      // Send them to OUR login page, not straight to the identity provider.
      //
      // Calling signIn() here jumps to /auth/login/<backend>/, which 404s with
      // "Backend not found" whenever SSO is not configured for the tenant — a Django
      // error page instead of a sign-in form. Our page offers both SSO and password,
      // and only shows the providers this tenant actually has.
      return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
    }
    return (
      <main className="page page--message">
        <h1>Not available for your account</h1>
        <p>
          You are signed in, but this area needs a higher level of access. Ask a
          platform administrator if you think that is wrong.
        </p>
      </main>
    );
  }

  return <>{children ?? <Outlet />}</>;
}
